import type { ComponentType } from 'react';
import { useLingui } from '@lingui/react/macro';
import { NewPathsIllustration } from '@worldcoo/ui/illustrations/static/new-paths';
import { OfferACauseIllustration } from '@worldcoo/ui/illustrations/static/offer-a-cause';
import { ReviewIllustration } from '@worldcoo/ui/illustrations/static/review';
import { SendIllustration } from '@worldcoo/ui/illustrations/static/send';
import { cn } from '@worldcoo/ui/lib/utils';
import { useNgoAreaUrl } from '@/lib/ngo-area';
import { useScrollStep } from '../use-scroll-step';
import { PillLink } from '../primitives';
import { NGO_APPLICATION_FORM_URL } from '../external-links';

type Step = {
	number: string;
	title: string;
	body: string;
	illustration: ComponentType<{ className?: string }>;
	/** Surface and text colours. */
	surface: string;
	tone: string;
	/** The last card only. */
	cta?: string;
	ctaHref?: string;
};

/** 275x330 and 354x424: breakpoint pairs rather than a CSS `scale`, which would blur the type. */
function StepCard({
	step,
	className,
	style,
	onAdvance,
}: {
	step: Step;
	className?: string;
	style?: React.CSSProperties;
	/** Absent on the last card. */
	onAdvance?: () => void;
}) {
	return (
		<article
			onClick={onAdvance}
			className={cn(
				'flex h-[330px] w-[275px] flex-col justify-end overflow-hidden rounded-[19px] p-8',
				'lg:h-[424px] lg:w-[354px] lg:rounded-3xl lg:p-10',
				onAdvance && 'cursor-pointer',
				step.surface,
				step.tone,
				className,
			)}
			style={style}
		>
			<step.illustration className="absolute top-6 left-8 h-auto w-[150px] lg:top-8 lg:left-10 lg:w-[197px]" />
			<span className="absolute top-6 right-6 text-xl leading-[1.5] tracking-[-0.02em] opacity-90 lg:top-8 lg:right-8 lg:text-2xl">
				{step.number}
			</span>
			<div className="relative flex flex-col gap-1">
				<h3 className="text-xl leading-[1.2] font-medium tracking-[-0.02em] lg:text-2xl">{step.title}</h3>
				<p className="text-sm leading-[1.5] lg:text-base">{step.body}</p>
				{step.cta &&
					step.ctaHref && (
						// So a card-level `onAdvance` added later cannot swallow this click.
						<a
							href={step.ctaHref}
							onClick={(event) => event.stopPropagation()}
							className="mt-3 w-fit rounded-sm text-[15px] font-medium tracking-[-0.02em] underline focus-visible:ring-2 focus-visible:ring-current focus-visible:outline-none"
						>
							{step.cta}
						</a>
					)}
			</div>
		</article>
	);
}

/**
 * A row on mobile, where a 132px column does not fit the pinned content and gets cropped. The ordinals
 * are the section's only keyboard control.
 */
function StepRail({ steps, active, onSelect }: { steps: Step[]; active: number; onSelect: (index: number) => void }) {
	return (
		<div className="flex shrink-0 flex-col-reverse items-center gap-2 lg:flex-row lg:items-center lg:gap-1.5">
			{/* Stacked, `self-stretch` lands each segment under its number without measuring. */}
			<div className="bg-ink/10 relative h-0.5 self-stretch rounded-full lg:h-[122px] lg:w-0.5 lg:self-auto">
				<div
					className="bg-brand absolute top-0 left-[var(--rail-offset)] h-full w-[var(--rail-segment)] rounded-full transition-all duration-500 lg:top-[var(--rail-offset)] lg:left-0 lg:h-[var(--rail-segment)] lg:w-full"
					style={
						{
							'--rail-segment': `${100 / steps.length}%`,
							'--rail-offset': `${(active * 100) / steps.length}%`,
						} as React.CSSProperties
					}
				/>
			</div>
			<ol className="flex gap-3 lg:flex-col lg:gap-2">
				{steps.map((step, index) => (
					<li key={step.number}>
						<button
							type="button"
							onClick={() => onSelect(index)}
							aria-current={index === active ? 'step' : undefined}
							className={cn(
								'focus-visible:ring-ink cursor-pointer rounded-sm text-base leading-[1.5] font-medium tracking-[-0.02em] transition-opacity focus-visible:ring-2 focus-visible:outline-none',
								index === active ? 'opacity-100' : 'opacity-20 hover:opacity-60',
							)}
						>
							{step.number}
						</button>
					</li>
				))}
			</ol>
		</div>
	);
}

/** Scroll-driven at BOTH breakpoints, as the mobile frame confirms: one layout, row to column. */
export function HowToCollaborate() {
	const { t } = useLingui();
	const ngoArea = useNgoAreaUrl();

	const steps: Step[] = [
		{
			number: '01',
			title: t`Send your application`,
			body: t`Fill in the form with your organisation's basic information.`,
			illustration: SendIllustration,
			surface: 'bg-apricot',
			tone: 'text-black',
		},
		{
			number: '02',
			title: t`Assessment`,
			body: t`Our team will check that you meet the minimum transparency criteria. If something is missing, don't worry — we'll tell you what to correct so you can apply again.`,
			illustration: ReviewIllustration,
			surface: 'bg-ink',
			tone: 'text-white',
		},
		{
			number: '03',
			title: t`Agreement and activation`,
			body: t`We sign the collaboration agreement and you're all set!`,
			illustration: OfferACauseIllustration,
			surface: 'bg-brand',
			tone: 'text-black',
		},
		{
			number: '04',
			title: t`Still have questions before you start?`,
			body: t`Join the next live information session.`,
			illustration: NewPathsIllustration,
			surface: 'bg-olive',
			tone: 'text-black',
			cta: t`Register`,
			ctaHref: ngoArea('/onboarding'),
		},
	];

	const { ref, active, enabled, goTo } = useScrollStep(steps.length);
	const last = steps.length - 1;

	return (
		/* 70vh per card, as `steps * 100vh` makes the page read as stuck. */
		<section
			ref={ref}
			className="relative"
			// Reduced motion reads no travel, so it gets none.
			style={enabled ? { height: `calc(100vh + ${(steps.length - 1) * 70}vh)` } : undefined}
		>
			{/* `min-h-screen`, or a taller column is CROPPED rather than measured; the clip is for rotated cards. */}
			<div className="sticky top-0 flex min-h-screen items-center overflow-hidden">
				{/* The rotated cards reach past their box, so a smaller gap almost touched the rail. */}
				<div className="mx-auto flex w-full max-w-[1440px] flex-col items-center gap-12 px-4 lg:flex-row lg:items-center lg:justify-between lg:gap-10 lg:px-16">
					<div className="flex max-w-[646px] flex-col items-start gap-4">
						<h2 className="text-3xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem]">
							{t`How do you start collaborating?`}
						</h2>
						<p className="text-base leading-[1.5]">
							{t`If you're looking for funding for your social projects, the Solidarity Round-Up and our digital tools connect you with the customers and employees of large companies. Your projects join our database so partner companies can select them according to their values.`}
						</p>
						<PillLink variant="ink" href={NGO_APPLICATION_FORM_URL}>{t`Register your organisation`}</PillLink>
					</div>

					<div className="relative h-[330px] w-[275px] shrink-0 lg:h-[424px] lg:w-[354px]">
						{steps.map((step, index) => {
							const offset = index - active;
							const isPast = offset < 0;
							return (
								<StepCard
									key={step.number}
									step={step}
									onAdvance={offset === 0 && index < last ? () => goTo(index + 1) : undefined}
									className="absolute inset-0 transition-all duration-500 ease-out"
									style={{
										transform: isPast
											? 'translateY(-32px) scale(0.94) rotate(-6deg)'
											: `translateY(${offset * 12}px) rotate(${offset * 4}deg) scale(${1 - offset * 0.03})`,
										opacity: isPast ? 0 : 1,
										zIndex: steps.length - Math.abs(offset),
										pointerEvents: offset === 0 ? 'auto' : 'none',
									}}
								/>
							);
						})}
					</div>

					<StepRail steps={steps} active={active} onSelect={goTo} />
				</div>
			</div>
		</section>
	);
}
